// Draws a set of objects into any 2D context at any scale, using the board's
// own draw routines: PNG export, version previews and template thumbnails.
import type { BoardObject } from '../../shared/types.ts';
import { connectorBounds, connectorPoints } from '../engine/connectors.ts';
import { drawObject } from '../engine/draw/index.ts';
import { drawConnector, FRAME_TITLE_GAP_PX, FRAME_TITLE_PX } from '../engine/draw/content.ts';
import type { ImageCache } from '../engine/images.ts';
import { drawText, type TextLayouts } from '../text/placement.ts';
import { aabb, objectMatrix, union, type Rect } from '../util/geometry.ts';

export interface SceneDeps {
  get: (id: string) => BoardObject | undefined;
  layouts: TextLayouts;
  images: ImageCache;
}

/** Scenes draw frame titles at a fixed world size, as if at 100%. */
const TITLE_BAND = FRAME_TITLE_PX + FRAME_TITLE_GAP_PX + 6;

/** World bounds of an object as drawn (connector paths, frame titles included). */
export function drawnBounds(o: BoardObject, get: SceneDeps['get']): Rect {
  if (o.type === 'connector') return connectorBounds(o, get);
  const r = aabb(o);
  return o.type === 'frame' ? { ...r, y: r.y - TITLE_BAND, h: r.h + TITLE_BAND } : r;
}

export function sceneBounds(objs: readonly BoardObject[], get: SceneDeps['get'], pad = 0): Rect | null {
  const r = union(objs.map((o) => drawnBounds(o, get)));
  return r && { x: r.x - pad, y: r.y - pad, w: r.w + 2 * pad, h: r.h + 2 * pad };
}

/** Paint order: z, then id (the store's order). */
export function paintOrder(objs: readonly BoardObject[]): BoardObject[] {
  return [...objs].sort((a, b) => (a.z < b.z ? -1 : a.z > b.z ? 1 : a.id < b.id ? -1 : 1));
}

/** Draws `objs` (in paint order) with world point (x, y) at the canvas origin. */
export function drawScene(ctx: CanvasRenderingContext2D, objs: readonly BoardObject[], deps: SceneDeps, view: { x: number; y: number; scale: number }): void {
  const { scale } = view;
  const tx = -view.x * scale, ty = -view.y * scale;
  const dc = { lod: false, zoom: 1, scale, images: deps.images, editable: false };
  for (const o of objs) {
    if (o.type === 'connector') {
      ctx.setTransform(scale, 0, 0, scale, tx, ty);
      drawConnector(ctx, o, connectorPoints(o, deps.get), false, scale);
      continue;
    }
    const [a, b, c, d, e, f] = objectMatrix(o, scale, tx, ty);
    ctx.setTransform(a, b, c, d, e, f);
    drawObject(ctx, o, dc);
    const placed = deps.layouts.get(o);
    if (placed) {
      const r = placed.region;
      ctx.save();
      ctx.beginPath();
      ctx.rect(r.x, r.y, r.w, r.h);
      ctx.clip();
      drawText(ctx, placed, scale);
      ctx.restore();
    }
  }
  ctx.setTransform(1, 0, 0, 1, 0, 0);
}

/** Renders objects into a canvas of at most maxW × maxH CSS px (thumbnails). */
export function renderThumbnail(canvas: HTMLCanvasElement, objs: readonly BoardObject[], deps: SceneDeps, background = '#FFFFFF'): void {
  const dpr = devicePixelRatio || 1;
  const cssW = canvas.clientWidth || canvas.width, cssH = canvas.clientHeight || canvas.height;
  canvas.width = Math.round(cssW * dpr);
  canvas.height = Math.round(cssH * dpr);
  const ctx = canvas.getContext('2d')!;
  ctx.fillStyle = background;
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  const bounds = sceneBounds(objs, deps.get, 24);
  if (!bounds) return;
  const scale = Math.min(canvas.width / bounds.w, canvas.height / bounds.h);
  const x = bounds.x - (canvas.width / scale - bounds.w) / 2;
  const y = bounds.y - (canvas.height / scale - bounds.h) / 2;
  drawScene(ctx, paintOrder(objs), deps, { x, y, scale });
}
